import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Spin, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import styled from 'styled-components';

import { CollectionChannelConfig } from 'constants/constants';
import {
  debtCollectionApi,
  DebtCollectionDashboardDto,
  ChannelStatsDto,
  CollectionChannel,
} from '../debtCollectionApi';

const StatValue = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin: 8px 0 4px;
  color: #272b41;
`;

const StatLabel = styled.span`
  font-size: 14px;
  color: #9299b8;
`;

const StatCard = styled(Card)`
  text-align: center;
  .ant-card-body {
    padding: 20px;
  }
`;

const DashboardTab: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<DebtCollectionDashboardDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const { data: result } = await debtCollectionApi.getDashboard();
        setData(result);
      } catch (error) {
        console.error('Error fetching dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) return null;

  const successRate = data.successRate != null ? `${(data.successRate * 100).toFixed(1)}%` : '-';

  const channelColumns = [
    {
      title: t('debt-collection:dashboard.channel', 'Channel'),
      dataIndex: 'channel',
      key: 'channel',
      render: (val: CollectionChannel) => {
        const cfg = CollectionChannelConfig[val];
        return cfg ? <Tag color={cfg.color}>{cfg.title}</Tag> : '-';
      },
    },
    {
      title: t('debt-collection:dashboard.sent', 'Sent'),
      dataIndex: 'totalSent',
      key: 'totalSent',
    },
    {
      title: t('debt-collection:dashboard.failed', 'Failed'),
      dataIndex: 'totalFailed',
      key: 'totalFailed',
      render: (val: number) => (val > 0 ? <span style={{ color: '#ff4d4f' }}>{val}</span> : val),
    },
    {
      title: t('debt-collection:dashboard.skipped', 'Skipped'),
      dataIndex: 'totalSkipped',
      key: 'totalSkipped',
    },
  ];

  const stepColumns = [
    {
      title: t('debt-collection:dashboard.step', 'Step'),
      dataIndex: 'stepOrder',
      key: 'stepOrder',
    },
    {
      title: t('debt-collection:dashboard.count', 'Active CashFlows'),
      dataIndex: 'count',
      key: 'count',
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <StatCard>
            <FeatherIcon icon="alert-circle" size={24} style={{ color: '#ff4d4f' }} />
            <StatValue>{data.totalOverdue ?? 0}</StatValue>
            <StatLabel>{t('debt-collection:dashboard.total-overdue', 'Total Overdue')}</StatLabel>
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <StatCard>
            <FeatherIcon icon="trending-up" size={24} style={{ color: '#faad14' }} />
            <StatValue>{data.activeEscalations ?? 0}</StatValue>
            <StatLabel>{t('debt-collection:dashboard.active-escalations', 'Active Escalations')}</StatLabel>
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <StatCard>
            <FeatherIcon icon="send" size={24} style={{ color: '#1890ff' }} />
            <StatValue>{data.messagesSentToday ?? 0}</StatValue>
            <StatLabel>{t('debt-collection:dashboard.messages-today', 'Messages Today')}</StatLabel>
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <StatCard>
            <FeatherIcon icon="check-circle" size={24} style={{ color: '#52c41a' }} />
            <StatValue>{successRate}</StatValue>
            <StatLabel>{t('debt-collection:dashboard.success-rate', 'Success Rate')}</StatLabel>
          </StatCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title={t('debt-collection:dashboard.channel-stats', 'Channel Statistics')}>
            <Table
              dataSource={data.channelStats ?? []}
              columns={channelColumns}
              rowKey={(r: ChannelStatsDto) => String(r.channel)}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title={t('debt-collection:dashboard.step-breakdown', 'Step Breakdown')}>
            <Table
              dataSource={data.stepBreakdown ?? []}
              columns={stepColumns}
              rowKey={(r) => String(r.stepOrder)}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardTab;
